import type { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { Sites } from '@ps/platform-message-framework/common/Sites';
import { ResolverTag } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import { useResolver } from '@ps/web-shared-components';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';

const MAX_ACTIVE_GAMES = 4;

enum MessageType {
  REGISTER = 'game_register',
  UNREGISTER = 'game_unregister',
  END = 'game_end',
  MESSAGE = 'message',
  ACTIVE_GAMES_COUNT = 'active_games_count'
}

interface ActiveTableConfig {
  brand: BrandTypes;
  site: Sites;
  origin: string;
  country: string | undefined;
  xiid: string;
  timezone: string;
  newtab: boolean;
  gametype: string;
  endpoint?: string;
  hasvideosupport?: boolean;
  flags?: number;
  scriptId?: string | number;
}

interface TabConfig {
  url: string;
  windowName: string;
  id: string;
  status: string;
}

interface BrowserTab {
  id: string;
  status: string;
  url: string;
  windowName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: any;
}

interface AccrossTabsParentConfig {
  onHandshakeCallback: () => void;
  onChildCommunication: (data: ChildCommunicationData) => void;
  onChildDisconnect?: () => void;
}

interface AccrossTabsParent {
  getOpenedTabs: () => BrowserTab[];
  openNewTab: (config: TabConfig) => void;
  broadCastTo: (id: string, data: ActiveTableConfig) => void;
}

interface ChildCommunicationData {
  id: string;
  type: MessageType;
  message: string | number;
}

let parentInstance: AccrossTabsParent | null = null;
let broadcastAfterOpen: ActiveTableConfig | undefined;
const createParentInstance = async (config: AccrossTabsParentConfig) => {
  // @ts-ignore - Ignore warning for missing types for accross-tabs
  const AcrossTabs = await import(/* webpackChunkName: "across-tabs" */ 'across-tabs');
  if (!parentInstance) {
    parentInstance = new AcrossTabs.default.Parent(config);
  }
  return parentInstance;
};

const useOpenActiveTable = () => {
  const resolver = useResolver(ResolverTag.WebPoker);
  const parentRef = useRef<AccrossTabsParent | null>(null);
  const activeTableCount = useSelector(select.activeTable.count);
  const { modal } = useDispatch<Dispatch>();

  const openActiveTableLimitModal = useCallback(
    () => modal.enqueue({ path: '/poker/active-table-limit' }),
    [modal]
  );

  const {
    activeTable: { setActiveTablesCount }
  } = useDispatch<Dispatch>();

  const openActiveTable = async (data?: ActiveTableConfig) => {
    if (activeTableCount > MAX_ACTIVE_GAMES && data) {
      openActiveTableLimitModal();
      return;
    }

    if (!parentRef.current && canUseDOM) {
      parentRef.current = await createParentInstance({
        onHandshakeCallback: () => {
          if (broadcastAfterOpen) {
            sendPostMessage(broadcastAfterOpen);
            broadcastAfterOpen = undefined;
          }
        },
        onChildCommunication: (data: ChildCommunicationData) => {
          if (data.type === MessageType.ACTIVE_GAMES_COUNT) {
            setActiveTablesCount(+data.message);
          }
        },
        onChildDisconnect: () => {
          setActiveTablesCount(0);
        }
      });
    }

    const tabConfig: TabConfig = {
      url: resolver,
      windowName: 'ActiveTable',
      id: 'ActiveTable',
      status: 'open'
    };

    const currentTabs = parentRef?.current?.getOpenedTabs();

    if (currentTabs?.length) {
      currentTabs[0].ref.focus();

      if (data) {
        sendPostMessage(data);
      }
      return;
    }

    if (parentRef.current) {
      broadcastAfterOpen = data;
      parentRef.current.openNewTab(tabConfig);
    }
  };

  const sendPostMessage = (data: ActiveTableConfig) => {
    if (parentRef.current) {
      const currentTabs = parentRef?.current?.getOpenedTabs();
      parentRef.current.broadCastTo(currentTabs[0].id, data);
    }
  };

  return {
    openActiveTable,
    activeTableCount
  };
};

export default useOpenActiveTable;
